import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { Button } from '@/components/ui/button';
import { REVIEW_NOTE_MAX } from '@matter_extractor/shared';
import { type ReactNode, useEffect, useRef } from 'react';

/**
 * The small editor every note of the review opens in place (relecture.md §5.5,
 * §6, §8, §9): a field, a button that says what it does, and « Annuler ».
 *
 * Uncontrolled, like the forms of this package: the field is read when the
 * button is pressed, and the caller decides what the text means - an empty one
 * clears a note, but cannot add one.
 *
 * It takes the focus when it opens, once: the gesture that opened it was a click
 * on « noter », and the next thing anybody does is type.
 */

export interface ReviewNoteEditorProps {
  /** The accessible name of the field - « Note de la case », « Note de l'extrait ». */
  readonly label: string;
  readonly initial?: string;
  readonly placeholder?: string;
  /** What the confirming button says: « Enregistrer », « Je valide »... */
  readonly confirm?: string;
  /** The tone of the confirming button: a validation is green, a refusal red. */
  readonly tone?: 'default' | 'ok' | 'ko';
  readonly max?: number;
  readonly busy: boolean;
  readonly onSave: (text: string) => void;
  readonly onCancel: () => void;
  readonly children?: ReactNode;
}

const TONE = {
  default: 'border-bleu-nuit text-bleu-nuit hover:bg-bleu-nuit hover:text-white',
  ok: 'border-vert-deep text-vert-deep hover:bg-vert-deep hover:text-white',
  ko: 'border-rouge-deep text-rouge-deep hover:bg-rouge-deep hover:text-white',
} as const;

export function ReviewNoteEditor({
  label,
  initial = '',
  placeholder,
  confirm = 'Enregistrer',
  tone = 'default',
  max = REVIEW_NOTE_MAX,
  busy,
  onSave,
  onCancel,
  children,
}: ReviewNoteEditorProps) {
  const field = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    field.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div className="flex flex-col gap-1.5">
      <textarea
        ref={field}
        data-testid={REVIEW_TEST_IDS.noteInput}
        aria-label={label}
        defaultValue={initial}
        placeholder={placeholder}
        maxLength={max}
        rows={2}
        className="min-h-14 w-full resize-y rounded-md border border-gray-300 px-2 py-1.5 text-sm focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      {children}
      <div className="flex flex-wrap gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid={REVIEW_TEST_IDS.noteSave}
          disabled={busy}
          className={`h-7 px-2.5 text-caption ${TONE[tone]}`}
          onClick={() => onSave(field.current?.value ?? '')}
        >
          {confirm}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          data-testid={REVIEW_TEST_IDS.noteCancel}
          className="h-7 px-2.5 text-caption text-gray-700"
          onClick={onCancel}
        >
          Annuler
        </Button>
      </div>
    </div>
  );
}
